import { useState } from 'react';
import type { CollectionAdminItem } from '@bailafm/shared';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { ApiClientError } from '@/lib/api';

interface CollectionDeleteDialogProps {
  collection: CollectionAdminItem | null;
  onClose(): void;
  onConfirm(collection: CollectionAdminItem): Promise<void>;
}

export function CollectionDeleteDialog({
  collection,
  onClose,
  onConfirm,
}: CollectionDeleteDialogProps) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleConfirm() {
    if (!collection) return;
    setPending(true);
    setError(null);
    try {
      await onConfirm(collection);
      toast.success('Colección eliminada');
      onClose();
    } catch (err) {
      const message =
        err instanceof ApiClientError && err.code === 'etag_mismatch'
          ? 'Otro usuario modificó esta colección. Recarga la lista y vuelve a intentarlo.'
          : err instanceof ApiClientError
            ? err.message
            : 'No se pudo eliminar la colección.';
      setError(message);
      toast.error(message);
    } finally {
      setPending(false);
    }
  }

  const preview = collection
    ? (collection.label.es ?? collection.label.ca ?? collection.label.en ?? collection.id)
    : '';

  return (
    <AlertDialog
      open={Boolean(collection)}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Eliminar colección</AlertDialogTitle>
          <AlertDialogDescription>
            ¿Quieres eliminar la colección <strong>{preview}</strong>? Esta
            acción no se puede deshacer.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              void handleConfirm();
            }}
            disabled={pending}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {pending ? 'Eliminando…' : 'Eliminar'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
